import { useMemo } from "react";
import {
  Section,
  HeroCard,
  Swatch,
  Bullet,
  Pill,
} from "./ReportUIPrimitives";
import styles from "./BenchmarkViewer.module.css";

const NA = "Not available";

const openUrlSafely = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const BrandIntelligenceViewer = ({ data = {}, view = null }) => {
  const {
    homepage_url,
    brand_assets = {},
    tone_and_voice = {},
    homepage_content = {},
    brand_summary = {},
  } = data;

  const colorPalette = useMemo(
    () => (Array.isArray(brand_assets.color_palette) ? brand_assets.color_palette : []),
    [brand_assets],
  );

  // Pick primary font: first typography entry, prefer role containing "heading"
  const primaryFontName = useMemo(() => {
    const list = Array.isArray(brand_assets.typography) ? brand_assets.typography : [];
    const heading = list.find((t) => /heading/i.test(t?.role || ""));
    return (heading || list[0])?.name || null;
  }, [brand_assets]);

  const typographyRows = useMemo(
    () => (Array.isArray(brand_assets.typography) ? brand_assets.typography : []),
    [brand_assets],
  );

  const announcementText =
    homepage_content.announcement_text || null;

  // UI components: string array — render as rows labeled "Component 1", "Component 2"…
  const uiComponentRows = useMemo(
    () => (Array.isArray(brand_assets.ui_components) ? brand_assets.ui_components : []),
    [brand_assets],
  );

  const trustElements = useMemo(
    () => (Array.isArray(brand_assets.trust_elements) ? brand_assets.trust_elements : []),
    [brand_assets],
  );

  const personality = useMemo(
    () => (Array.isArray(tone_and_voice.brand_personality) ? tone_and_voice.brand_personality : []),
    [tone_and_voice],
  );

  const dos = useMemo(
    () => (Array.isArray(tone_and_voice.dos) ? tone_and_voice.dos : []),
    [tone_and_voice],
  );

  const donts = useMemo(
    () => (Array.isArray(tone_and_voice.donts) ? tone_and_voice.donts : []),
    [tone_and_voice],
  );

  const examplePhrases = useMemo(
    () => (Array.isArray(tone_and_voice.example_phrases) ? tone_and_voice.example_phrases : []),
    [tone_and_voice],
  );

  const heroImageUrl = useMemo(() => {
    const list = Array.isArray(brand_assets.hero_images) ? brand_assets.hero_images : [];
    return list[0]?.url || null;
  }, [brand_assets]);

  const renderBrandAssetsSection = () => (
    <Section title="Brand Assets">
      {/* Hero row: homepage link + hero image */}
      <div className={styles.brandFrame}>
        <div className={styles.brandHeroRow}>
          <div className={styles.brandHeroText}>
            <span className={styles.brandPanelLabel}>Shopify URL:</span>
            {homepage_url ? (
              <button
                type="button"
                className={styles.brandHomepageLink}
                onClick={() => openUrlSafely(homepage_url)}
              >
                {homepage_url.replace(/^https?:\/\//, "")}
              </button>
            ) : (
              <span className={styles.brandPanelValue}>—</span>
            )}
            {brand_summary.description ? (
              <p className={styles.brandPanelDescription}>{brand_summary.description}</p>
            ) : null}
          </div>
          {heroImageUrl ? (
            <img
              className={styles.brandHeroImage}
              src={heroImageUrl}
              alt="Homepage preview"
            />
          ) : null}
        </div>

        {/* Meta strip */}
        <div className={styles.brandPanelMeta}>
          <div className={styles.brandPanelMetaBlock}>
            <span className={styles.brandPanelMetaLabel}>Palette</span>
            <span className={styles.brandPanelMetaValue}>
              {colorPalette.length ? `${colorPalette.length} colors` : "No palette data"}
            </span>
          </div>
          <div className={styles.brandPanelMetaBlock}>
            <span className={styles.brandPanelMetaLabel}>Primary font</span>
            <span className={styles.brandPanelMetaValue}>{primaryFontName || NA}</span>
          </div>
          <div className={styles.brandPanelMetaBlock}>
            <span className={styles.brandPanelMetaLabel}>Announcement</span>
            <span className={styles.brandPanelMetaValue}>
              {announcementText ? "Live" : "Missing"}
            </span>
          </div>
        </div>

        {/* Color swatches */}
        {colorPalette.length > 0 ? (
          <div className={styles.brandPaletteRow}>
            {colorPalette.map((color, i) => (
              <Swatch
                key={`${color?.hex || i}-${i}`}
                hex={color?.hex}
                name={color?.role}
              />
            ))}
          </div>
        ) : (
          <p className={styles.brandPaletteEmpty}>Color palette not available</p>
        )}
      </div>

      {/* Logo / favicon links */}
      {(brand_assets.logo_url || brand_assets.favicon_url) ? (
        <div className={styles.detailList}>
          {brand_assets.logo_url ? (
            <div className={styles.brandCopyRow}>
              <span className={styles.brandCopyLabel}>Primary logo</span>
              <button
                type="button"
                className={styles.brandHomepageLink}
                onClick={() => openUrlSafely(brand_assets.logo_url)}
              >
                {brand_assets.logo_url}
              </button>
            </div>
          ) : null}
          {brand_assets.favicon_url ? (
            <div className={styles.brandCopyRow}>
              <span className={styles.brandCopyLabel}>Favicon</span>
              <button
                type="button"
                className={styles.brandHomepageLink}
                onClick={() => openUrlSafely(brand_assets.favicon_url)}
              >
                {brand_assets.favicon_url}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Typography rows */}
      {typographyRows.length > 0 ? (
        <div className={styles.brandCopyStack}>
          {typographyRows.map((font, i) => (
            <div key={`${font?.name || i}-${i}`} className={styles.brandCopyRow}>
              <span className={styles.brandCopyLabel}>{font?.role || `Font ${i + 1}`}</span>
              <span className={styles.brandCopyValue}>{font?.name || NA}</span>
            </div>
          ))}
        </div>
      ) : null}

      {/* UI components rows */}
      {uiComponentRows.length > 0 ? (
        <div className={styles.brandCopyStack}>
          {uiComponentRows.map((item, i) => (
            <div key={`${item}-${i}`} className={styles.brandCopyRow}>
              <span className={styles.brandCopyLabel}>{`Component ${i + 1}`}</span>
              <span className={styles.brandCopyValue}>{item}</span>
            </div>
          ))}
        </div>
      ) : null}

      {/* Visual direction */}
      {(brand_assets.visual_style || brand_assets.image_style) ? (
        <HeroCard
          title="Visual Direction"
          subtitle={brand_assets.visual_style || undefined}
        >
          <div className={styles.bulletStack}>
            {brand_assets.image_style ? (
              <Bullet>{brand_assets.image_style}</Bullet>
            ) : null}
          </div>
        </HeroCard>
      ) : null}

      {/* Trust elements */}
      {trustElements.length > 0 ? (
        <HeroCard title="Trust Elements">
          <div className={styles.bulletStack}>
            {trustElements.map((item, i) => (
              <Bullet key={`${item}-${i}`}>{item}</Bullet>
            ))}
          </div>
        </HeroCard>
      ) : null}
    </Section>
  );

  const renderToneAndVoiceSection = () => (
    <Section title="Tone & Voice">
      {/* Brand Expression card */}
      <HeroCard
        title="Brand Expression"
        subtitle={brand_summary.vibe || tone_and_voice.emotional_positioning || undefined}
      >
        <div className={styles.brandPillRow}>
          {tone_and_voice.detected_tone ? (
            <Pill text={tone_and_voice.detected_tone} />
          ) : null}
          {personality.length ? (
            personality.map((item, i) => (
              <Pill key={`${item}-${i}`} text={item} />
            ))
          ) : null}
          {!tone_and_voice.detected_tone && !personality.length ? (
            <span className={styles.emptyNote}>No tone guidance yet.</span>
          ) : null}
        </div>
      </HeroCard>

      {/* Writing style / audience language rows */}
      <div className={styles.brandCopyStack}>
        {tone_and_voice.writing_style ? (
          <div className={styles.brandCopyRow}>
            <span className={styles.brandCopyLabel}>Writing style</span>
            <span className={styles.brandCopyValue}>{tone_and_voice.writing_style}</span>
          </div>
        ) : null}
        {tone_and_voice.audience_language ? (
          <div className={styles.brandCopyRow}>
            <span className={styles.brandCopyLabel}>Audience language</span>
            <span className={styles.brandCopyValue}>{tone_and_voice.audience_language}</span>
          </div>
        ) : null}
      </div>

      {/* Do */}
      {dos.length > 0 ? (
        <HeroCard title="Do">
          <div className={styles.bulletStack}>
            {dos.map((item, i) => (
              <Bullet key={`${item}-${i}`}>{item}</Bullet>
            ))}
          </div>
        </HeroCard>
      ) : null}

      {/* Don't */}
      {donts.length > 0 ? (
        <HeroCard title="Don't">
          <div className={styles.bulletStack}>
            {donts.map((item, i) => (
              <Bullet key={`${item}-${i}`}>{item}</Bullet>
            ))}
          </div>
        </HeroCard>
      ) : null}

      {/* Example phrases */}
      {examplePhrases.length > 0 ? (
        <HeroCard title="Example phrases">
          <div className={styles.brandPillRow}>
            {examplePhrases.map((item, i) => (
              <Pill key={`${item}-${i}`} text={item} />
            ))}
          </div>
        </HeroCard>
      ) : null}

      {/* Brand summary */}
      {brand_summary.description ? (
        <div className={styles.summaryCard}>
          <p className={styles.summaryLabel}>Brand summary</p>
          <p className={styles.summaryText}>{brand_summary.description}</p>
        </div>
      ) : null}
    </Section>
  );

  if (view === "brand-assets") return renderBrandAssetsSection();
  if (view === "tone-and-voice") return renderToneAndVoiceSection();
  return (
    <div className={styles.container}>
      {renderBrandAssetsSection()}
      {renderToneAndVoiceSection()}
    </div>
  );
};

export default BrandIntelligenceViewer;
